import { computed } from 'vue'

import { appBranding } from '@/lib/features'

export function useBranding() {
  // @ts-expect-error: Extract common active and config check
  const activeBrandingConfig = computed(() => (appBranding?.isActive ? appBranding.config : null))

  // Accept both absolute URLs (https://…) and app-relative paths (/datenschutz).
  // Branding may point the privacy policy at an in-app route, which URL.canParse
  // rejects on its own — resolving against the origin keeps those links working.
  const isValidUrl = (url: string): boolean =>
    URL.canParse(url) || URL.canParse(url, window.location.origin)

  const platformAssetUrl = computed(() => activeBrandingConfig.value?.assetUrl ?? null)

  const platform = computed<'markero' | 'bottimmo' | 'remax' | 'smg' | 'turkoglu' | 'falc'>(
    () => activeBrandingConfig.value?.platform ?? null
  )

  const isPlatformBottimmo = computed(() => platform.value === 'bottimmo')

  const isPlatformMarkero = computed(() => platform.value === 'markero')

  const isPlatformSmg = computed(() => platform.value === 'smg')

  const isPlatformFalc = computed(() => platform.value === 'falc')

  const legalCompanyName = computed(() => activeBrandingConfig.value?.legalCompanyName ?? null)

  const isPlatformRemax = computed(() => platform.value === 'remax')

  const isPlatformTurkoglu = computed(() => platform.value === 'turkoglu')

  const readablePlatformName = computed(
    () => activeBrandingConfig.value?.readablePlatformName ?? null
  )

  const dataPrivacyUrl = computed(() => {
    const url = activeBrandingConfig.value?.dataPrivacyUrl
    return url && isValidUrl(url) ? url : null
  })

  const publicDataPrivacyUrl = computed(() => activeBrandingConfig.value?.publicDataPrivacyUrl)

  const publicTOCandLicenceUrl = computed(() => activeBrandingConfig.value?.publicTOCandLicenceUrl)

  const imprintUrl = computed(() => activeBrandingConfig.value?.imprintUrl)

  const primaryColor = computed(() => activeBrandingConfig.value?.primary)

  // Use an enum or const object for better type safety and readability
  const DEFAULT_COLORS = {
    PRIMARY: '#07BEB8',
    LOGIN_BACKGROUND: '#0896c9',
  } as const

  const isValidHexColor = (color: string | null | undefined): boolean =>
    color ? /^#[0-9A-F]{6}$/i.test(color) : false

  const createColorWithOpacity = (color: string, opacity: number): string => {
    const hex = Math.round(opacity * 255)
      .toString(16)
      .padStart(2, '0')
    return `${color}${hex}`
  }

  const gradientColors = computed(() => {
    const config = activeBrandingConfig.value
    return {
      start: isValidHexColor(config?.primary) ? config.primary : DEFAULT_COLORS.PRIMARY,
      end: isValidHexColor(config?.loginBackgroundColor)
        ? config.loginBackgroundColor
        : DEFAULT_COLORS.LOGIN_BACKGROUND,
    }
  })

  const dashboardBoxesBackground = computed(() => {
    const config = activeBrandingConfig.value
    const defaultColors = [
      {
        base: '#0A372F',
        border: '#062822',
      },
      {
        base: '#0E443A',
        border: '#0A322A',
      },
      {
        base: '#125145',
        border: '#0E4035',
        polygon: '#0E5C4D',
      },
      {
        base: '#175E51',
        border: '#1B4D43',
      },
      {
        base: 'linear-gradient(180deg, #e46c2c 100%, #ec9048 100%)',
      },
    ]

    const boxes = {}

    for (let i = 0; i < 5; i++) {
      const customColor = config && config?.dashboardBoxesBackground?.[i]

      boxes[`box${i + 1}`] = customColor || defaultColors[i]
    }

    return boxes
  })

  const staticGradientColors = computed(() => {
    const config = activeBrandingConfig.value
    return {
      start: createColorWithOpacity(
        isValidHexColor(config?.primary) ? config.primary : DEFAULT_COLORS.PRIMARY,
        0.6
      ),
      end: createColorWithOpacity(
        isValidHexColor(config?.loginBackgroundColor)
          ? config.loginBackgroundColor
          : DEFAULT_COLORS.LOGIN_BACKGROUND,
        0.6
      ),
    }
  })

  return {
    platformAssetUrl,
    platform,
    isPlatformBottimmo,
    isPlatformMarkero,
    isPlatformRemax,
    isPlatformSmg,
    isPlatformFalc,
    isPlatformTurkoglu,
    legalCompanyName,
    readablePlatformName,
    dataPrivacyUrl,
    publicDataPrivacyUrl,
    publicTOCandLicenceUrl,
    imprintUrl,
    primaryColor,
    gradientColors,
    staticGradientColors,
    dashboardBoxesBackground,
  }
}
